'use client';

import { getTeamQuota } from '@/app/apis';
import { useFetch, useMembershipRevision } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { UserQuotaInfo } from '@/app/stores/slices/user-info';
import { isOptionAllowed } from '@/app/utils';
import { tasksGetUserQuota } from '../apis';

/**
 * 在 create 下仅挂载一次，统一拉取错峰余量并写入 store，避免多个 Settings 重复请求。
 * - 个人额度：活动前旗舰版、活动后所有已登录用户通过 tasksGetUserQuota 获取
 * - 企业版：通过 getTeamQuota 获取团队错峰余量
 * 提交任务后 invalidateUserQuota 递增会触发重拉。
 */
export const UserQuotaSync = () => {
  const isNewMembership = useMembershipRevision();
  const { userId, canFetchPersonalQuota, isTeamSpace } = useStore((state) => {
    return {
      userId: state.userInfo?.id,
      canFetchPersonalQuota:
        Boolean(state.userInfo?.id) &&
        isNewMembership !== undefined &&
        (isNewMembership
          ? isOptionAllowed('schedule_mode', 'nopeak', state.userInfo?.subs_plan, undefined, true)
          : state.userInfo?.subs_plan === 'ultimate_v2'),
      isTeamSpace: state.isTeamSpace,
    };
  });
  const userQuotaRefreshVersion = useStore((state) => state.userQuotaRefreshVersion);
  const updateUserQuota = useStore((state) => state.updateUserQuota);

  useFetch(() => tasksGetUserQuota({}), {
    ready: !isTeamSpace && canFetchPersonalQuota,
    refreshDeps: [userQuotaRefreshVersion, userId, isNewMembership],
    onSuccess: (result) => {
      if (result?.data != null) {
        updateUserQuota(result.data as UserQuotaInfo);
      }
    },
  });

  useFetch(getTeamQuota, {
    ready: isTeamSpace,
    refreshDeps: [userQuotaRefreshVersion],
    onSuccess: (result) => {
      const nopeak_quota = result?.data?.quota?.nopeak_quota;
      if (nopeak_quota != null) {
        updateUserQuota({ nopeak_quota });
      }
    },
  });

  return null;
};
